import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store/index.ts";
import Layout from "../../components/Layout.tsx";
import Title from "../../components/Title.tsx";
import { Panel } from "../../components/panel/panel.jsx";
import Loader from "../../components/Loader.tsx";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import "dayjs/locale/es";
import {
  getMissingBoxList,
  MissingBoxRecord,
} from "../../services/missing-box-partner.service.ts";
import { toCamelCase } from "../../hooks/toCameCase.tsx";
import { useAppSettings } from "../../hooks/useAppSettings.tsx";
import FilterMissingBox from "../../components/missing-box-partner/FilterMissingBox.tsx";
import PaginationMissingBox from "../../components/missing-box-partner/PaginationMissingBox.tsx";
import {
  setRecords,
  setTotal,
  setPage,
  setSortBy,
  setDefaultSort,
  setShowDeleteModal,
  setCurrentRecord,
} from "../../store/slices/missingBoxSlice.ts";
import { Tooltip } from "@mui/material";
import EmptyImage from "../../assets/img/ic_content.svg";
import ModalContainer from "../../components/ModalContainer.tsx";
import DeleteMissingBoxModal from "../../components/missing-box-partner/DeleteMissingBoxModal.tsx";

dayjs.locale("es");

interface MissingBoxParams {
  limit: number;
  offset: number;
  param: string;
  sort: string;
  order: string;
  incidentDate: string;
  branchId: string;
}

const thStyle: React.CSSProperties = {
  padding: "0.75rem 1rem",
  fontSize: "11px",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  color: "#6b7280",
  background: "#f8fafc",
  borderBottom: "2px solid #e5e7eb",
  cursor: "pointer",
  whiteSpace: "nowrap",
};

const computeFortnightProgress = (startUnix: number, endUnix: number) => {
  const now = new Date();
  const getMonthEnd = (y: number, m: number) => new Date(y, m + 1, 0).getDate();

  const normStart = (d: Date) => {
    const day = d.getDate();
    return day <= 15 ? new Date(d.getFullYear(), d.getMonth(), 1) : new Date(d.getFullYear(), d.getMonth(), 16);
  };
  const normEnd = (d: Date) => {
    const day = d.getDate();
    return day <= 15 ? new Date(d.getFullYear(), d.getMonth(), 15) : new Date(d.getFullYear(), d.getMonth(), getMonthEnd(d.getFullYear(), d.getMonth()));
  };
  const nextStart = (d: Date) => {
    const next = new Date(d.getTime() + 86400000);
    return normStart(next);
  };

  const s = normStart(new Date(startUnix * 1000));
  const e = normEnd(new Date(endUnix * 1000));

  let total = 0;
  let completed = 0;
  let cursor = new Date(s);
  while (cursor <= e) {
    const periodEnd = cursor.getDate() <= 15
      ? new Date(cursor.getFullYear(), cursor.getMonth(), 15)
      : new Date(cursor.getFullYear(), cursor.getMonth(), getMonthEnd(cursor.getFullYear(), cursor.getMonth()));
    total++;
    if (now > periodEnd) completed++;
    cursor = nextStart(periodEnd);
  }
  if (completed > total) completed = total;
  return { completed, total };
};

const getProgressBadgeStyle = (completed: number, total: number): React.CSSProperties => {
  const base: React.CSSProperties = {
    padding: "0.25rem 0.65rem", borderRadius: "999px",
    fontSize: "11px", fontWeight: 600,
    display: "inline-flex", alignItems: "center", gap: "0.3rem",
    whiteSpace: "nowrap",
  };
  if (total > 0 && completed >= total)
    return { ...base, backgroundColor: "#dcfce7", color: "#15803d", border: "1px solid #bbf7d0" };
  if (completed > 0)
    return { ...base, backgroundColor: "#fef3c7", color: "#92400e", border: "1px solid #fde68a" };
  return { ...base, backgroundColor: "#fee2e2", color: "#b91c1c", border: "1px solid #fecaca" };
};

const AllMissingBox = () => {
  dayjs.locale("es");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  useAppSettings();

  const {
    records,
    total,
    page,
    limit,
    query,
    sortBy,
    defaultSort,
    showDeleteModal,
    currentRecord,
  } = useSelector((state: RootState) => state.missingBox);

  const missingBoxParams = useMemo((): MissingBoxParams => {
    const offset = page * limit;
    const param = query.includes("param=")
      ? query.split("param=")[1]?.split("&")[0] || ""
      : "";
    const incidentDate = query.includes("incidentDate=")
      ? query.split("incidentDate=")[1]?.split("&")[0] || ""
      : "";
    const branchId = query.includes("branchId=")
      ? query.split("branchId=")[1]?.split("&")[0] || ""
      : "";

    return { limit, offset, param, sort: sortBy, order: defaultSort.toUpperCase(), incidentDate, branchId };
  }, [page, limit, query, sortBy, defaultSort]);

  const fetchMissingBox = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const response = await getMissingBoxList({
        limit: missingBoxParams.limit,
        offset: missingBoxParams.offset,
        order: missingBoxParams.order as "ASC" | "DESC",
        sort: missingBoxParams.sort,
        param: missingBoxParams.param,
        incidentDate: missingBoxParams.incidentDate || undefined,
        branchId: missingBoxParams.branchId ? Number(missingBoxParams.branchId) : undefined,
      });
      dispatch(setRecords(response.data || []));
      dispatch(setTotal(response.total || 0));
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Error desconocido al cargar faltantes en caja";
      setError(errorMessage);
      console.error("Error fetching missing box:", err);
    } finally {
      setLoading(false);
    }
  }, [dispatch, missingBoxParams]);

  const handleSort = useCallback(
    (column: string): void => {
      const sortOrder = sortBy !== column ? "asc" : defaultSort === "asc" ? "desc" : "asc";
      dispatch(setSortBy(column));
      dispatch(setPage(0));
      dispatch(setDefaultSort(sortOrder));
    },
    [dispatch, sortBy, defaultSort]
  );

  const handleDelete = useCallback(
    (record: MissingBoxRecord) => {
      dispatch(setCurrentRecord(record));
      dispatch(setShowDeleteModal(true));
    },
    [dispatch]
  );

  const handleDeleteSuccess = useCallback(() => {
    fetchMissingBox();
  }, [fetchMissingBox]);

  useEffect(() => {
    fetchMissingBox();
  }, [fetchMissingBox]);

  const getIconSort = () => (
    <i className={`fa-solid fa-sort-${defaultSort === "asc" ? "up" : "down"}`} />
  );

  const formatDate = (value: any): string => {
    if (value === null || value === undefined || value === "") return "-";
    try {
      if (typeof value === "number" || (!isNaN(Number(value)) && String(value).length > 6)) {
        const d = dayjs.unix(Number(value));
        return d.isValid() ? d.format("DD MMM YYYY") : "-";
      }
      const d = dayjs(value);
      return d.isValid() ? d.format("DD MMM YYYY") : "-";
    } catch {
      return "-";
    }
  };

  return (
    <Layout>
      <Title
        baseTitle="Faltantes en caja"
        basePath="/faltantes-caja"
        title="Listado de faltantes en caja"
      />

      <div className="card border-0 m-4 rounded shadow-sm">
        <div className="tab-pane fade show active">
          <div className="card-body">
            <Panel>
              <FilterMissingBox />

              <div
                className="table-responsive mb-3"
                style={{
                  height: "500px",
                  overflowY: "auto",
                  overflowX: "auto",
                  borderRadius: "12px",
                  border: "1px solid #e5e7eb",
                }}
              >
                <table
                  className="table table-panel text-nowrap align-middle mb-0"
                  style={{ borderCollapse: "separate", borderSpacing: 0 }}
                >
                  <thead>
                    <tr style={{ position: "sticky", top: 0, zIndex: 1 }}>
                      <th style={thStyle} onClick={() => handleSort("id")}>
                        Id {sortBy === "id" && getIconSort()}
                      </th>
                      <th style={thStyle} onClick={() => handleSort("partner")}>
                        Colaborador {sortBy === "partner" && getIconSort()}
                      </th>
                      <th style={thStyle} onClick={() => handleSort("branch")}>
                        Sucursal {sortBy === "branch" && getIconSort()}
                      </th>
                      <th style={thStyle} onClick={() => handleSort("incidentDate")}>
                        Fecha incidencia {sortBy === "incidentDate" && getIconSort()}
                      </th>
                      <th style={thStyle} onClick={() => handleSort("totalMissingAmount")}>
                        Monto total {sortBy === "totalMissingAmount" && getIconSort()}
                      </th>
                      <th style={thStyle} onClick={() => handleSort("amountPerPeriod")}>
                        Por quincena {sortBy === "amountPerPeriod" && getIconSort()}
                      </th>
                      <th style={{ ...thStyle, cursor: "default" }}>Periodo</th>
                      <th style={{ ...thStyle, cursor: "default" }}>Progreso</th>
                      <th style={{ ...thStyle, textAlign: "right", cursor: "default" }}>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {records?.length > 0 ? (
                      records.map((record: MissingBoxRecord, idx: number) => {
                        const tdStyle: React.CSSProperties = {
                          padding: "0.65rem 1rem",
                          fontSize: "13px",
                          color: "#374151",
                          borderBottom: "1px solid #f3f4f6",
                          verticalAlign: "middle",
                        };
                        const partnerName = record.partner?.person
                          ? toCamelCase(
                              `${record.partner.person.firstName || ""} ${record.partner.person.lastName || ""} ${record.partner.person.secondLastName || ""}`.trim()
                            )
                          : null;
                        const { completed, total } = computeFortnightProgress(
                          record.startPeriodDate,
                          record.endPeriodDate
                        );

                        return (
                          <tr
                            key={record.id}
                            style={{ background: idx % 2 === 0 ? "#ffffff" : "#fafafa", transition: "background 0.1s" }}
                            onMouseEnter={e => (e.currentTarget.style.background = "#f0f9ff")}
                            onMouseLeave={e => (e.currentTarget.style.background = idx % 2 === 0 ? "#ffffff" : "#fafafa")}
                          >
                            <td style={{ ...tdStyle, fontWeight: 600, color: "#6b7280", fontSize: 12 }}>
                              #{record.id}
                            </td>
                            <td style={{ ...tdStyle, fontWeight: 500 }}>
                              {partnerName || <span style={{ color: "#9ca3af" }}>—</span>}
                            </td>
                            <td style={tdStyle}>{record.branch?.name || "-"}</td>
                            <td style={{ ...tdStyle, color: "#6b7280", fontSize: 12 }}>
                              {formatDate(record.incidentDate)}
                            </td>
                            <td style={{ ...tdStyle, fontWeight: 700, color: "#111827" }}>
                              ${Number(record.totalMissingAmount).toFixed(2)}
                            </td>
                            <td style={{ ...tdStyle, color: "#6b7280" }}>
                              ${Number(record.amountPerPeriod).toFixed(2)}
                            </td>
                            <td style={{ ...tdStyle, color: "#6b7280", fontSize: 12 }}>
                              <span style={{ display: "inline-flex", alignItems: "center", gap: "0.3rem" }}>
                                <i className="fa-solid fa-calendar-range" style={{ fontSize: 9, color: "#9ca3af" }} />
                                {formatDate(record.startPeriodDate)} — {formatDate(record.endPeriodDate)}
                              </span>
                            </td>
                            <td style={tdStyle}>
                              <span style={getProgressBadgeStyle(completed, total)}>
                                <i className="fa-solid fa-circle" style={{ fontSize: 6 }} />
                                {completed}/{total} Cobrada(s)
                              </span>
                            </td>
                            <td style={{ ...tdStyle, textAlign: "right" }}>
                              <div style={{ display: "inline-flex", gap: "0.4rem" }}>
                                <Tooltip title="Ver / Editar" arrow>
                                  <button
                                    type="button"
                                    onClick={() => navigate(`/faltantes-caja/editar/${record.id}`)}
                                    style={{
                                      display: "inline-flex", alignItems: "center", gap: "0.3rem",
                                      padding: "0.3rem 0.7rem", borderRadius: "8px",
                                      fontSize: "12px", fontWeight: 600, cursor: "pointer",
                                      background: "#eff6ff", color: "#1d4ed8",
                                      border: "1px solid #bfdbfe",
                                    }}
                                    onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = "#dbeafe"; }}
                                    onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = "#eff6ff"; }}
                                  >
                                    <i className="fa fa-eye" style={{ fontSize: 11 }} /> Ver
                                  </button>
                                </Tooltip>
                                <Tooltip title="Eliminar" arrow>
                                  <button
                                    type="button"
                                    onClick={() => handleDelete(record)}
                                    style={{
                                      display: "inline-flex", alignItems: "center", gap: "0.3rem",
                                      padding: "0.3rem 0.7rem", borderRadius: "8px",
                                      fontSize: "12px", fontWeight: 600, cursor: "pointer",
                                      background: "#fef2f2", color: "#b91c1c",
                                      border: "1px solid #fecaca",
                                    }}
                                    onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = "#fee2e2"; }}
                                    onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = "#fef2f2"; }}
                                  >
                                    <i className="fa fa-trash" style={{ fontSize: 11 }} />
                                  </button>
                                </Tooltip>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan={9}>
                          <div style={{ padding: "3rem 1rem", textAlign: "center" }}>
                            <img src={EmptyImage} alt="" style={{ width: 140, opacity: 0.6, marginBottom: "1rem" }} />
                            <p style={{ fontWeight: 600, color: "#374151", margin: "0 0 0.25rem" }}>
                              {error ? "Error al cargar faltantes" : "Sin faltantes en caja"}
                            </p>
                            <p style={{ color: "#9ca3af", fontSize: 13, margin: 0 }}>
                              {error ? error : "No se encontraron registros con los filtros aplicados"}
                            </p>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <PaginationMissingBox />
            </Panel>
          </div>
        </div>
      </div>

      <Loader isLoading={loading} />

      <ModalContainer
        open={showDeleteModal}
        title="Eliminar faltante en caja"
        closeFn={() => dispatch(setShowDeleteModal(false))}
      >
        <DeleteMissingBoxModal
          closeFn={() => dispatch(setShowDeleteModal(false))}
          record={currentRecord}
          onSuccess={handleDeleteSuccess}
        />
      </ModalContainer>
    </Layout>
  );
};

export default AllMissingBox;
